/**
 * Comparator function for arrays of objects that performs a custom equality check.
 *
 * The comparator:
 * - First checks if the arrays are the same reference, or if their lengths differ.
 * - For each item, compares the specified `selectedProps` deeply (using JSON.stringify).
 * - For all other properties, performs a shallow comparison.
 * - Returns true if all items and their properties match according to these rules, otherwise false.
 *
 * Useful for optimizing React.memo or useMemo dependencies when only certain object properties should trigger updates.
 *
 * @param selectedProps - Set of property names to compare deeply for each object in the array.
 * @returns A function that takes two arrays and returns true if they are considered equal.
 */
export const customArrayCompare = <T extends Record<string, any>>(
  selectedProps: Set<string>
) => {
  return (prev: T[], next: T[]) => {
    if (prev.length !== next.length) return false;

    for (let i = 0; i < prev.length; i++) {
      const prevItem = prev[i];
      const nextItem = next[i];

      // Shallow compare remaining keys
      const prevKeys = Object.keys(prevItem);
      const nextKeys = Object.keys(nextItem);
      if (prevKeys.length !== nextKeys.length) return false;

      for (const key of prevKeys) {
        if (selectedProps.has(key)) {
          try {
            if (
              JSON.stringify(prevItem[key]) !== JSON.stringify(nextItem[key])
            ) {
              return false;
            }
          } catch (error) {
            if (prevItem[key] !== nextItem[key]) {
              return false;
            }
          }
        } else {
          if (prevItem[key] !== nextItem[key]) {
            return false;
          }
        }
      }
    }

    return true;
  };
};
